import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../utils/apiError'
import { appToast } from '../../../components/AppToast'
import {
  createSupportTicket,
  getCurrentUserProfile,
  getSupportTicket,
  updateSupportTicket,
  uploadSupportTicketAttachment,
  deleteSupportTicketAttachment,
} from '../api/supportTicketApi'
import {
  INITIAL_TICKET_FORM,
  SUPPORT_TICKET_CATEGORY_OPTIONS,
  SUPPORT_TICKET_PRIORITY_OPTIONS,
} from '../types'

function getLocalUserFallback() {
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}')
    const rawId = stored?.id ?? stored?.user_id ?? stored?.userId
    const normalizedId = Number(rawId)
    return {
      id: Number.isFinite(normalizedId) && normalizedId > 0 ? String(normalizedId) : '0',
      name: stored?.name || stored?.userName || 'Current User',
      email: stored?.email || '',
      department: stored?.departmentName || stored?.department || '',
      role: stored?.roleName || stored?.role || '',
      isAdmin: Boolean(stored?.is_admin),
      privileges: Array.isArray(stored?.privileges) ? stored.privileges.map((item) => String(item).toUpperCase()) : [],
    }
  } catch {
    return { id: '0', name: 'Current User', email: '', department: '', role: '', isAdmin: false, privileges: [] }
  }
}

export function useSupportTicketEntry() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const ticketId = searchParams.get('id') ? Number(searchParams.get('id')) : null
  const isEditMode = Boolean(ticketId && ticketId > 0)

  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [resetKey, setResetKey] = useState(0)
  const savingRef = useRef(false)
  const [reporter, setReporter] = useState(getLocalUserFallback())

  // New files staged locally before upload
  const [newAttachments, setNewAttachments] = useState([])
  // Existing attachments already saved on server
  const [existingAttachments, setExistingAttachments] = useState([])

  const [form, setForm] = useState(INITIAL_TICKET_FORM)

  useEffect(() => {
    let active = true

    async function loadBootstrap() {
      setIsLoading(true)
      setError('')
      try {
        const [profile, existingTicket] = await Promise.all([
          getCurrentUserProfile().catch(() => null),
          isEditMode ? getSupportTicket(ticketId).catch(() => null) : Promise.resolve(null),
        ])

        if (!active) return

        const fallback = getLocalUserFallback()
        const resolvedReporter = {
          ...fallback,
          id: profile?.id ? String(profile.id) : fallback.id,
          name: profile?.name || fallback.name,
          email: profile?.email || fallback.email,
          department: profile?.department_name || fallback.department,
          role: profile?.role_name || fallback.role,
          isAdmin: Boolean(profile?.is_admin ?? fallback.isAdmin),
          privileges: Array.isArray(profile?.privileges)
            ? profile.privileges.map((item) => String(item).toUpperCase())
            : fallback.privileges,
        }
        setReporter(resolvedReporter)

        if (existingTicket) {
          setForm({
            subject: existingTicket.subject || '',
            description: existingTicket.description || '',
            category: existingTicket.category || 'ISSUE',
            priority: existingTicket.priority || 'MEDIUM',
          })
          setExistingAttachments(Array.isArray(existingTicket.attachments) ? existingTicket.attachments : [])
        }
      } catch (bootstrapError) {
        if (!active) return
        setError(extractApiErrorMessage(bootstrapError, 'Failed to load support ticket form.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadBootstrap()
    return () => {
      active = false
    }
  }, [ticketId, isEditMode])

  const fieldErrors = useMemo(() => {
    const errors = {}
    if (!form.subject.trim()) errors.subject = 'Subject is required.'
    if (!form.description.trim()) errors.description = 'Description is required.'
    if (!form.category) errors.category = 'Category is required.'
    if (!form.priority) errors.priority = 'Priority is required.'
    return errors
  }, [form.subject, form.description, form.category, form.priority])

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function resetForm() {
    setForm(INITIAL_TICKET_FORM)
    setNewAttachments([])
    setExistingAttachments([])
    setError('')
    setSuccess('')
    setResetKey((prev) => prev + 1)
  }

  async function submitTicket({ continueEditing = false } = {}) {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    if (Object.keys(fieldErrors).length > 0) {
      const validationMsg = 'Please fill all required fields before submitting.'
      setError(validationMsg)
      appToast.error(validationMsg)
      savingRef.current = false
      return
    }

    setIsSaving(true)
    try {
      let ticketRecord

      if (isEditMode) {
        // PATCH existing ticket
        ticketRecord = await updateSupportTicket(ticketId, {
          subject: form.subject,
          description: form.description,
          category: form.category,
          priority: form.priority,
        })
      } else {
        // POST new ticket
        ticketRecord = await createSupportTicket({
          subject: form.subject,
          description: form.description,
          category: form.category,
          priority: form.priority,
          requesterId: reporter.id,
          requesterName: reporter.name,
          requesterEmail: reporter.email,
        })
      }

      // Upload any new staged files
      for (const file of newAttachments) {
        await uploadSupportTicketAttachment(ticketRecord.id, file)
      }

      const ticketNum = ticketRecord?.ticketNo ? `Ticket ${ticketRecord.ticketNo}` : 'Ticket'

      if (continueEditing) {
        const successMsg = `${ticketNum} created successfully! Ready for next entry.`
        setSuccess(successMsg)
        appToast.success(successMsg)
        resetForm()
        if (ticketId) {
          navigate('/support/tickets/entry', { replace: true })
        }
      } else {
        const successMsg = isEditMode
          ? `${ticketNum} updated successfully.`
          : `${ticketNum} submitted successfully.`
        setSuccess(successMsg)
        appToast.success(successMsg)
        navigate('/support/tickets/list')
      }
    } catch (submitError) {
      const errorMsg = extractApiErrorMessage(
        submitError,
        isEditMode ? 'Failed to update support ticket.' : 'Failed to submit support ticket.'
      )
      setError(errorMsg)
      appToast.error(errorMsg)
    } finally {
      savingRef.current = false
      setIsSaving(false)
    }
  }

  function addAttachments(files) {
    setNewAttachments((prev) => [...prev, ...files])
  }

  function removeNewAttachment(index) {
    setNewAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  async function removeExistingAttachment(attachment) {
    if (!ticketId) return
    setError('')
    try {
      await deleteSupportTicketAttachment(ticketId, attachment.id)
      setExistingAttachments((prev) => prev.filter((a) => a.id !== attachment.id))
    } catch (deleteError) {
      setError(extractApiErrorMessage(deleteError, 'Failed to remove attachment.'))
    }
  }

  return {
    state: {
      isLoading,
      isSaving,
      error,
      success,
      form,
      reporter,
      categoryOptions: SUPPORT_TICKET_CATEGORY_OPTIONS,
      priorityOptions: SUPPORT_TICKET_PRIORITY_OPTIONS,
      fieldErrors,
      isEditMode,
      newAttachments,
      existingAttachments,
      resetKey,
    },
    actions: {
      setField,
      addAttachments,
      removeNewAttachment,
      removeExistingAttachment,
      submitTicket: () => submitTicket({ continueEditing: false }),
      submitTicketAndContinue: () => submitTicket({ continueEditing: true }),
      resetForm,
      cancel: () => navigate('/support/tickets/list'),
    },
  }
}
